import type { I18n, MessageDescriptor } from '@lingui/core';
import { RemoteUIProvider } from '../i18n/remote';
import { formatDate, formatNumber } from '../i18n/format';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { canRespondToNormalizedInteraction } from '../lib/normalized-session-presenter';
import ReadOnlyInteraction from './ReadOnlyInteraction';
import { PROTOCOL_VERSION } from '@borgee/agent-remote-protocol';
import '@borgee/agent-remote-web/styles.css';
import { useEffect, useMemo, useState, type Ref } from 'react';
import {
  BulbOutlined,
  CloseOutlined,
  CloudDownloadOutlined,
  CodeOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileTextOutlined,
  RetweetOutlined,
  SearchOutlined,
  SwapOutlined,
  ToolOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  ACTIVITY_STATUS_COMPLETED,
  ACTIVITY_STATUS_FAILED,
  ACTIVITY_STATUS_IN_PROGRESS,
  ACTIVITY_STATUS_PENDING,
  ACTIVITY_STATUS_WAITING,
  useAgentActivityStore,
  type AgentActivityEntry,
  type AgentPanelState,
} from '../stores/useAgentActivityStore';
import StopTurnButton, { type StopOutcome } from './StopTurnButton';
import type { ActivePerson } from '../lib/channel-activity';
import type { AgentActivityPlanEntry } from '../types/ws-frames';
import type { BackgroundRun } from '../stores/useAgentActivityStore';
import { ApiError, stopBackgroundRun } from '../lib/api';
import { useAuthStore } from '../stores/useAuthStore';
import { useAgentStore } from '../stores/useAgentStore';
import { usePresenceStore } from '../stores/usePresenceStore';
import { submitInteractionResponse } from '../lib/interaction-responses';
import { getNormalizedTaskTarget } from '../lib/normalized-session-controls';
import { createTimelineRenderModel, InteractionPanel, TimelineItemRenderer } from '@borgee/agent-remote-web/react';
import { useNormalizedActivityStore } from '../stores/useNormalizedActivityStore';

const ELAPSED_MINUTE_MS = 60_000;

function formatElapsedLabel(i18n: I18n, elapsedMinutes: number) {
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  return hours > 0
    ? `${formatNumber(i18n, hours)}:${String(minutes).padStart(2, '0')}`
    : i18n._(msg`${formatNumber(i18n, minutes)}m`);
}

export interface VisibleBackgroundRun extends BackgroundRun {
  agentId: string;
  agentName: string;
  parentActivityLabel?: string;
}

export type ActivityPanelPerson = ActivePerson;

const STATUS_WORD: Record<ActivityPanelPerson['status'], MessageDescriptor> = {
  waiting: msg`Waiting for you`,
  working: msg`Working`,
  typing: msg`Typing`,
};

/**
 * One block per agent, over the message list.
 *
 * It floats rather than growing beside the list: a sibling that grows shrinks
 * the list's visible box while the browser keeps its scroll offset, moving a
 * reader away from the newest message without their having scrolled, and nothing
 * in the list's scroll handling compensates for a sibling changing size. The
 * thread panel scrolls a different element from the channel view, and floating
 * costs neither of them a pixel.
 *
 * Only this component and its blocks read the activity map, and they exist only
 * while the panel is open, so a report about work nobody is looking at costs one
 * string comparison in the collapsed line and nothing else.
 */
export default function ActivityPanel({
  channelId,
  panelId,
  agents,
  outcomes,
  onOutcome,
  onClose,
  panelRef,
  stopInBlocks,
  backgroundRuns,
  onDismissBackgroundRun,
}: {
  channelId: string;
  panelId: string;
  agents: ActivityPanelPerson[];
  outcomes: ReadonlyMap<string, StopOutcome>;
  onOutcome: (agentId: string, outcome: StopOutcome) => void;
  onClose: () => void;
  panelRef: Ref<HTMLDivElement>;
  /** False while the bar already offers the one agent's stop; the action is offered once. */
  stopInBlocks: boolean;
  backgroundRuns: VisibleBackgroundRun[];
  onDismissBackgroundRun: (runId: string) => void;
}) {
  const { i18n } = useLingui();
  const title = i18n._(msg`Active now`);
  return (
    <div
      className="activity-panel"
      id={panelId}
      ref={panelRef}
      role="region"
      aria-label={title}
      tabIndex={-1}
    >
      <div className="activity-panel-header">
        <span className="activity-panel-title">{title}</span>
        <button type="button" className="activity-panel-close" aria-label={i18n._(msg`Close`)} onClick={onClose}>
          <CloseOutlined aria-hidden="true" />
        </button>
      </div>
      {backgroundRuns.length > 0 && (
        <section className="background-runs" aria-label={i18n._(msg`Background work`)}>
          {backgroundRuns.map(run => (
            <BackgroundRunRow
              key={run.runId}
              channelId={channelId}
              run={run}
              onDismiss={() => onDismissBackgroundRun(run.runId)}
            />
          ))}
        </section>
      )}
      {agents.map(agent => (
        <AgentBlock
          key={agent.id}
          channelId={channelId}
          person={agent}
          outcome={outcomes.get(agent.id) ?? 'idle'}
          onOutcome={onOutcome}
          showStop={stopInBlocks}
        />
      ))}
    </div>
  );
}

function BackgroundRunRow({
  channelId,
  run,
  onDismiss,
}: {
  channelId: string;
  run: VisibleBackgroundRun;
  onDismiss: () => void;
}) {
  const { i18n } = useLingui();
  const [outcome, setOutcome] = useState<'idle' | 'stopping' | 'stopped' | 'failed' | 'rate_limited'>('idle');
  const [now, setNow] = useState(() => Date.now());
  const active = run.state === 'running' || run.state === 'waiting';
  const canStop = active && run.stopSupported && outcome !== 'stopping';
  const disabledReason = !active
    ? i18n._(msg`This background run is no longer active`)
    : run.stopSupported
      ? undefined
      : run.stopUnsupportedReason || i18n._(msg`This provider does not support targeted background-run cancellation`);

  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), ELAPSED_MINUTE_MS);
    return () => window.clearInterval(timer);
  }, [active, run.runId, run.startedAt]);

  const stop = async () => {
    if (!canStop) return;
    setOutcome('stopping');
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const answer = await stopBackgroundRun(channelId, run.agentId, run.runId, controller.signal);
      setOutcome(answer.outcome === 'stopped' ? 'stopped' : 'failed');
    } catch (error) {
      setOutcome(error instanceof ApiError && error.status === 429 ? 'rate_limited' : 'failed');
    } finally {
      window.clearTimeout(timer);
    }
  };

  const stateLabel = {
    running: i18n._(msg`Running`),
    waiting: i18n._(msg`Waiting`),
    stopping: i18n._(msg`Stopping`),
    completed: i18n._(msg`Completed`),
    failed: i18n._(msg`Failed`),
    cancelled: i18n._(msg`Cancelled`),
    unknown: i18n._(msg`Unknown`),
  }[run.state];
  const stopLabel = outcome === 'stopping'
    ? i18n._(msg`Stopping…`)
    : outcome === 'stopped'
      ? i18n._(msg`Stop requested`)
      : outcome === 'rate_limited'
        ? i18n._(msg`Already stopping`)
        : outcome === 'failed'
          ? i18n._(msg`Could not stop`)
          : i18n._(msg`Stop`);
  const startedLabel = formatDate(i18n, run.startedAt, { hour: '2-digit', minute: '2-digit' });
  const endTimestamp = active ? now : (run.finishedAt ?? now);
  const elapsedMinutes = Math.max(0, Math.floor((endTimestamp - run.startedAt) / ELAPSED_MINUTE_MS));
  const elapsedLabel = formatElapsedLabel(i18n, elapsedMinutes);

  return (
    <div className="background-run" data-state={run.state} data-run-id={run.runId}>
      <div className="background-run-copy">
        <span className="background-run-label">{run.label || i18n._(msg`Background work`)}</span>
        <span className="background-run-meta">{run.agentName} · {stateLabel}</span>
        <span className="background-run-timing"><Trans>Started {startedLabel} · {elapsedLabel}</Trans></span>
        {run.parentActivityLabel && (
          <span className="background-run-parent"><Trans>Started by {run.parentActivityLabel}</Trans></span>
        )}
        {run.reason && <span className="background-run-reason">{run.reason}</span>}
      </div>
      <button
        type="button"
        className="activity-stop-btn"
        disabled={!canStop}
        title={disabledReason}
        onClick={stop}
      >
        {stopLabel}
      </button>
      <button type="button" className="background-run-dismiss" onClick={onDismiss}>
        <Trans>Hide</Trans></button>
    </div>
  );
}

/**
 * What one agent plans to do, what it is doing, and what it is saying, in the
 * order a reader wants them. A section with nothing in it is absent rather than
 * rendered empty.
 *
 * Each block subscribes to its own agent, so a report about one leaves the other
 * blocks untouched.
 */
function AgentBlock({
  channelId,
  person,
  outcome,
  onOutcome,
  showStop,
}: {
  channelId: string;
  person: ActivityPanelPerson;
  outcome: StopOutcome;
  onOutcome: (agentId: string, outcome: StopOutcome) => void;
  showStop: boolean;
}) {
  const { i18n } = useLingui();
  const agent = useAgentActivityStore(s => s.byChannel[channelId]?.[person.id]);
  const capacityNotice = useNormalizedActivityStore(s => s.noticesByChannel[channelId]);
  const normalizedTasks = useNormalizedActivityStore(s => s.byChannel[channelId]?.[person.id]);
  const normalized = useMemo(() => Object.entries(normalizedTasks ?? {}).flatMap(([taskId, task]) => createTimelineRenderModel(taskId, task.entries)), [normalizedTasks]);
  const currentUser = useAuthStore((state) => state.currentUser);
  const ownerId = useAgentStore((state) => state.loadFailed ? undefined : state.agents.find((owned) => owned.id === person.id)?.owner_id);
  const loadAgents = useAgentStore((state) => state.loadAgents);
  const connected = usePresenceStore((state) => state.connectionState === 'connected' && !!state.onlineUserIds[person.id]);
  const canRespond = connected && currentUser?.role === 'member' && currentUser.id === ownerId;
  const pending = Object.entries(normalizedTasks ?? {}).flatMap(([taskId, task]) => (task.pendingInteractions ?? []).map(({ request, sequence, displaySequence, expired, token }) => ({ taskId, request, token, sequence: displaySequence ?? sequence, active: !expired })));
  const hasPending = pending.length > 0;
  useEffect(() => {
    if (hasPending && connected && currentUser?.role === 'member') void loadAgents();
  }, [hasPending, connected, currentUser?.id, currentUser?.role, person.id, loadAgents]);

  return (
    <RemoteUIProvider><section className="activity-agent">
      <header className="activity-agent-header">
        <span className="activity-agent-name">{person.name}</span>
        <span className="activity-agent-line" data-line={person.status}>
          {i18n._(STATUS_WORD[person.status])}
        </span>
        {showStop && (
          <StopTurnButton
            className="activity-stop-btn"
            channelId={channelId}
            agentId={person.id}
            displayName={person.name}
            outcome={outcome}
            onOutcome={onOutcome}
          />
        )}
      </header>
      {capacityNotice && <p role="status"><Trans>New task activity is unavailable because earlier requests occupy the local display capacity.</Trans></p>}
      {agent && <AgentSections agent={agent} />}
      {pending.map(({ taskId, request, sequence, active, token }) => !active || !normalizedTasks?.[taskId]?.sessionScope?.bindingId || normalizedTasks?.[taskId]?.nativeCapabilitiesLive === false ? <ReadOnlyInteraction
        key={`${taskId}:${request.requestId}:${sequence}`} request={request} reason={i18n._(!active ? msg`Read-only · This earlier request is no longer active.` : msg`Read-only · This session is unavailable.`)}
      /> : <InteractionPanel
        key={`${taskId}:${request.requestId}:${sequence}`}
        request={request}
        onResponse={canRespond && active && !!normalizedTasks?.[taskId]?.sessionScope?.bindingId && canRespondToNormalizedInteraction(normalizedTasks?.[taskId], request) ? async (requestId, response) => {
          if (!token) throw new Error('Interaction is unavailable');
          const target = await getNormalizedTaskTarget(channelId, person.id, taskId, { token, requestId });
          const live = useNormalizedActivityStore.getState().byChannel[channelId]?.[person.id]?.[taskId];
          if (!live?.pendingInteractions?.some(pending => pending.token === token && !pending.expired && pending.request.requestId === requestId) || !canRespondToNormalizedInteraction(live, request)) throw new Error('Interaction is unavailable');
          const result = await submitInteractionResponse({
            channel_id: channelId, task_id: taskId, binding_id: target.binding_id, native_session_id: target.native_session_id, connection_generation: target.connection_generation,
            body: { protocolVersion: PROTOCOL_VERSION, type: 'interaction_response', payload: { agentId: person.id, requestId, response } },
          });
          useNormalizedActivityStore.getState().applyResolution(channelId, person.id, taskId, result, token);
        } : undefined}
      />)}
      {normalized.length > 0 && (
        <section className="activity-normalized-detail" aria-label={i18n._(msg`Execution detail`)}>
          {normalized.map(({ key, entry }) => <TimelineItemRenderer key={key} item={entry.item} />)}
        </section>
      )}
    </section></RemoteUIProvider>
  );
}

function AgentSections({ agent }: { agent: AgentPanelState }) {
  return (
    <>
      {agent.plan.length > 0 && <PlanSection plan={agent.plan} />}
      {agent.activities.length > 0 && <ActivitySection activities={agent.activities} />}
      {agent.answer !== '' && <OutputSection text={agent.answer} />}
    </>
  );
}

/**
 * The plan as a summary, with the whole list one click away.
 *
 * A plan can run to a dozen entries and only one of them is what the agent is
 * doing now, so the summary is what belongs in a panel a reader glances at.
 * Rows are keyed by position because the protocol gives entries no identity and
 * resends the list whole; the store keeps an unchanged entry's own object, so a
 * status flipping somewhere in the list leaves the rest of the rows alone.
 */
function PlanSection({ plan }: { plan: AgentActivityPlanEntry[] }) {
  const { i18n } = useLingui();
  const [showAll, setShowAll] = useState(false);
  const current = plan.find(entry => entry.status === 'in_progress');
  const done = plan.filter(entry => entry.status === 'completed').length;
  const left = plan.length - done - (current ? 1 : 0);

  return (
    <div className="activity-section">
      <button
        type="button"
        className="activity-plan-summary"
        aria-expanded={showAll}
        onClick={() => setShowAll(open => !open)}
      >
        {current ? (
          <>
            <span className="activity-plan-current">{current.label}</span>
            {left > 0 && <span className="activity-plan-rest"><Trans>{formatNumber(i18n, left)} to go</Trans></span>}
          </>
        ) : (
          <span className="activity-plan-rest"><Trans>{formatNumber(i18n, done)} of {formatNumber(i18n, plan.length)} done</Trans></span>
        )}
        <span className="activity-plan-toggle" aria-hidden="true">
          {showAll ? <UpOutlined aria-hidden="true" /> : <DownOutlined aria-hidden="true" />}
        </span>
      </button>
      {showAll && (
        <ol className="activity-plan-list">
          {plan.map((entry, index) => (
            <li key={index} className="activity-plan-item" data-status={entry.status}>
              {entry.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The tool calls, with a sub-agent's work indented under the call that spawned
 * it.
 *
 * A child can arrive before its parent — a permission request surfaces a call
 * before its own stream does — so an entry whose parent is not held yet renders
 * at the top level and moves under the parent once that arrives. Nothing is
 * dropped for want of a parent, including an entry caught in a cycle of parent
 * ids, which no traversal from the roots would reach.
 */
function ActivitySection({ activities }: { activities: AgentActivityEntry[] }) {
  const tree = useMemo(() => buildActivityTree(activities), [activities]);
  return (
    <ol className="activity-list">
      {tree.map(({ entry, children }) => (
        <li key={entry.id} className="activity-item-group">
          <ActivityRow entry={entry} />
          {children.length > 0 && (
            <ol className="activity-list activity-list-nested">
              {children.map(child => (
                <li key={child.id}>
                  <ActivityRow entry={child} />
                </li>
              ))}
            </ol>
          )}
        </li>
      ))}
    </ol>
  );
}

interface ActivityNode {
  entry: AgentActivityEntry;
  children: AgentActivityEntry[];
}

/**
 * Groups the entries one level deep: everything descended from a top-level
 * entry sits directly under it, however many parent links away it is.
 */
function buildActivityTree(activities: AgentActivityEntry[]): ActivityNode[] {
  const held = new Map(activities.map(entry => [entry.id, entry]));
  const nodes: ActivityNode[] = [];
  const byRoot = new Map<string, ActivityNode>();

  for (const entry of activities) {
    if (entry.parentId === '' || !held.has(entry.parentId)) {
      const node: ActivityNode = { entry, children: [] };
      nodes.push(node);
      byRoot.set(entry.id, node);
    }
  }

  for (const entry of activities) {
    if (byRoot.has(entry.id)) continue;
    let ancestor = held.get(entry.parentId);
    for (let hops = 0; ancestor && !byRoot.has(ancestor.id) && hops < activities.length; hops += 1) {
      ancestor = held.get(ancestor.parentId);
    }
    const root = ancestor && byRoot.get(ancestor.id);
    if (root) root.children.push(entry);
    else nodes.push({ entry, children: [] });
  }

  return nodes;
}

/**
 * One tool call.
 *
 * The description is preferred over the label because they are two strings with
 * two jobs: for a shell tool the label is the protocol's title, which is the raw
 * command line, and the description is what the agent said it was doing, which
 * is what a reader glancing at a panel wants. An entry with neither is a patch
 * to something never seen, and its reason is the only thing it has to say.
 */
function ActivityRow({ entry }: { entry: AgentActivityEntry }) {
  const { i18n } = useLingui();
  const Icon = ACTIVITY_KIND_ICONS[entry.kind] ?? ToolOutlined;
  const title = entry.description || entry.label;
  const status = backgroundStatusLabel(entry) ?? ACTIVITY_STATUS_LABELS[entry.status];
  const shown = entry.paths.slice(0, PATHS_SHOWN);

  return (
    <div className="activity-item" data-status={entry.status}>
      <span className="activity-item-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="activity-item-body">
        <span className="activity-item-line">
          {title !== '' && <span className="activity-item-title">{title}</span>}
          {(entry.background || entry.subagent) && (
            <span className="activity-item-badge">{entry.background ? i18n._(msg`background job`) : i18n._(msg`sub-agent`)}</span>
          )}
          <span className="activity-item-status">{status ? i18n._(status) : entry.status}</span>
        </span>
        {entry.detail !== '' && <span className="activity-item-detail">{entry.detail}</span>}
        {shown.length > 0 && (
          <span className="activity-item-detail">
            {shown.join(', ')}
            {entry.paths.length > shown.length && ` +${formatNumber(i18n, entry.paths.length - shown.length)}`}
          </span>
        )}
        {entry.reason !== '' && <span className="activity-item-reason">{entry.reason}</span>}
      </span>
    </div>
  );
}

/**
 * The tail of what the agent is saying.
 *
 * Plain text, never markdown: the answer arrives as a message of its own when
 * the turn ends and that is where it is rendered, while this changes several
 * times a second and parsing it that often would cost more than the whole rail
 * saves. It also keeps `dangerouslySetInnerHTML` off a surface fed straight
 * from an agent.
 *
 * The box is a fixed number of lines, so text that grows and shrinks does not
 * move the panel under the reader, and the newest line is the one pinned in
 * view rather than the oldest.
 */
function OutputSection({ text }: { text: string }) {
  return (
    <div className="activity-output">
      <span className="activity-output-text">{text}</span>
    </div>
  );
}

/** How many of an activity's files are named before the rest become a count. */
const PATHS_SHOWN = 3;

/**
 * The agent protocol's ten tool kinds. The enum is open — the next protocol
 * version makes that mandatory — so anything absent from this map is a tool
 * like any other and gets the generic icon.
 */
const ACTIVITY_KIND_ICONS: Record<string, typeof ToolOutlined> = {
  read: FileTextOutlined,
  edit: EditOutlined,
  delete: DeleteOutlined,
  move: SwapOutlined,
  search: SearchOutlined,
  execute: CodeOutlined,
  think: BulbOutlined,
  fetch: CloudDownloadOutlined,
  switch_mode: RetweetOutlined,
  other: ToolOutlined,
};

/** An unrecognised status shows the word the agent sent rather than being renamed or hidden. */
const ACTIVITY_STATUS_LABELS: Record<string, MessageDescriptor> = {
  [ACTIVITY_STATUS_PENDING]: msg`Queued`,
  [ACTIVITY_STATUS_IN_PROGRESS]: msg`Running`,
  [ACTIVITY_STATUS_WAITING]: msg`Waiting for you`,
  [ACTIVITY_STATUS_COMPLETED]: msg`Done`,
  [ACTIVITY_STATUS_FAILED]: msg`Failed`,
};

function backgroundStatusLabel(entry: AgentActivityEntry): MessageDescriptor | undefined {
  if (!entry.background) return undefined;
  switch (entry.backgroundState) {
    case 'starting': return msg`Starting in background`;
    case 'running': return msg`Running in background`;
    case 'waiting': return msg`Waiting in background`;
    case 'completed': return msg`Background completed`;
    case 'failed': return msg`Background failed`;
    default: return undefined;
  }
}
